/**
 * Tokens and rules for the shared HallMap.
 *
 * Kept as a string rather than a .css file so both Vite apps can inject it
 * without either one needing a CSS pipeline config for a workspace package.
 * The `--map-*` tokens are the whole contract: an app can retheme the map
 * without the component knowing.
 */
export const hallMapStyles = `
:root {
  --map-free: #d9e9e1;
  --map-free-line: #7fb69b;
  --map-free-ink: #2c6f51;
  /*
   * Three states used to share a grey and were impossible to tell apart. Each
   * now carries its own colour, and the colours mean what people expect:
   * green go, amber wait, red gone. Grey is reserved for the club's own
   * tables, which were never for sale in the first place.
   */
  /* Pending — in somebody's cart or awaiting a check. May yet come free. */
  --map-pending: #fbeecb;
  --map-pending-line: #d2a02a;
  --map-pending-ink: #8a6410;
  /* Taken — paid for. Not coming back. */
  --map-gone: #f7dedb;
  --map-gone-line: #c9564c;
  --map-gone-ink: #8f3529;
  /* Club tables, never on sale. */
  --map-off: #e6e9ec;
  --map-off-line: #b3bcc4;
  --map-off-ink: #8d99a3;
  --map-pick: #9c3464;
  --map-pick-ink: #ffffff;
  --map-power: #2a6595;
  /* Check-in desks: club furniture, never for sale, but people need to find
     them on the day — so they are drawn solid rather than as a faint outline. */
  --map-checkin: #e8dcc6;
  --map-checkin-line: #9a7f4e;
  --map-checkin-ink: #6d572f;
  /*
   * The backdrop is a white-background architectural plan drawn in thin dark
   * lines, so opacity acts almost entirely on those lines: composited over
   * the page, a black line lands at (1 - opacity) grey. At 0.6 that is 102
   * grey — washed out on a desktop monitor and effectively invisible on a
   * phone, which is how the building came to look absent altogether.
   *
   * 0.85 puts the lines at 38 grey: clearly a building, while still reading
   * as a backdrop behind the coloured tables rather than competing with them.
   */
  --map-plan-opacity: 0.85;
  --map-plan-filter: none;
}

.hallmap {
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
  /* Inherit, do not declare. touch-action does not cascade, and every touch
     lands on this SVG rather than on the stage around it — so a value here
     silently overrode the stage's, and the page kept scrolling even while the
     stage said 'none'. Inheriting makes the stage's decision the only one. */
  touch-action: inherit;
}

.hallmap__body { transition: fill 0.09s ease, stroke 0.09s ease; }

.hallmap__table.is-selectable { cursor: pointer; }
.hallmap__table.is-selectable:hover .hallmap__body { fill: var(--map-free-line); }
.hallmap__table.is-selected:hover .hallmap__body { fill: var(--map-pick); }
.hallmap__table:focus { outline: none; }
.hallmap__table:focus-visible .hallmap__body {
  stroke: var(--map-pick);
  stroke-width: 700;
}

@media (prefers-reduced-motion: reduce) {
  .hallmap__body { transition: none; }
}

/* ---- viewer: zoom, pan, fullscreen ---- */

.hallmapv { display: flex; flex-direction: column; gap: 8px; min-width: 0; }

.hallmapv__bar {
  display: flex; flex-wrap: wrap; gap: 8px;
  align-items: center; justify-content: space-between;
}

.hallmapv__caption {
  font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--map-ui-faint, #76879a);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.hallmapv__controls { display: flex; gap: 5px; flex-wrap: wrap; }

.hallmapv__controls button {
  appearance: none; font: inherit; font-size: 12px; line-height: 1;
  padding: 6px 10px; min-width: 32px; border-radius: 3px; cursor: pointer;
  border: 1px solid var(--map-ui-line, #ccd6de);
  background: var(--map-ui-bg, transparent);
  color: var(--map-ui-ink, inherit);
}
.hallmapv__controls button:hover:not(:disabled) { border-color: var(--map-pick); color: var(--map-pick); }
.hallmapv__controls button:disabled { opacity: 0.45; cursor: default; }
.hallmapv__controls button:focus-visible { outline: 2px solid var(--map-pick); outline-offset: 2px; }

/*
 * touch-action is load-bearing here.
 *
 * 'none' hands us every gesture, which is what pinch and drag want — but on a
 * phone in landscape the map is about 1.5x taller than the screen, so it
 * covers every pixel and there is nothing left to touch. The page cannot be
 * scrolled and the vendor is simply stuck.
 *
 * 'pan-y' keeps a vertical swipe with the browser, so the page can always be
 * scrolled away from the map. A horizontal drag still reaches us for panning,
 * and a two-finger pinch is not covered by pan-y either, so zoom still works.
 * The only thing given up is one-finger vertical panning of the map, which
 * pinch and the zoom buttons both cover.
 */
.hallmapv__stage {
  min-width: 0;
  touch-action: pan-y;
  /* Only offer the grab handle when there is somewhere to pan to. A fitted
     map is for clicking, and a grab cursor over it invites the wrong gesture. */
  cursor: default;
}
.hallmapv__stage.is-zoomed { cursor: grab; }
.hallmapv__stage.is-zoomed:active { cursor: grabbing; }

/* In fullscreen there is no page behind the map to scroll, and both a Close
   button and Escape are available, so taking every gesture is safe there. */
.hallmapv:fullscreen .hallmapv__stage,
.hallmapv.is-fullscreen .hallmapv__stage { touch-action: none; }

/* A second guarantee, independent of touch-action: the inline map never grows
   tall enough to fill the viewport on its own, so there is always page around
   it to scroll by. Landscape phones are the case that matters — there the
   viewport is short and the map would otherwise cover all of it. */
.hallmapv:not(:fullscreen):not(.is-fullscreen) .hallmap { max-height: 70vh; }

.hallmapv__hint {
  margin: 0; font-size: 11.5px;
  color: var(--map-ui-faint, #76879a);
}

/* The action bar carried into fullscreen. Pinned to the bottom so it is where
   a thumb already is, and so it never scrolls away from a vendor who has
   zoomed into a corner of the hall. */
.hallmapv__dock {
  position: sticky; bottom: 0; z-index: 2;
  display: flex; flex-wrap: wrap; gap: 10px;
  align-items: center; justify-content: space-between;
  padding: 11px 13px; margin-top: 10px;
  border: 1px solid var(--map-ui-line, #ccd6de);
  border-radius: 6px;
  background: var(--map-ui-sheet, #fff);
  box-shadow: 0 -2px 14px rgba(20, 30, 40, 0.13);
}

/* Fullscreen, and the fallback used when the browser refuses the request —
   an iframe without allow="fullscreen", for instance. */
.hallmapv:fullscreen,
.hallmapv.is-fullscreen {
  position: fixed; inset: 0; z-index: 9999;
  background: var(--map-ui-sheet, #ffffff);
  /* Tight padding: on a landscape phone the viewport is only ~412px tall, and
     every pixel of chrome comes straight out of the map. */
  padding: 8px; margin: 0; overflow: hidden;
  display: flex; flex-direction: column; gap: 6px;
}
/* The stage takes whatever is left and the SVG fills it. min-height:0 is the
   load-bearing part — without it a flex child refuses to shrink below its
   content and the map is sized to its natural height instead of the screen. */
.hallmapv:fullscreen .hallmapv__stage,
.hallmapv.is-fullscreen .hallmapv__stage {
  flex: 1 1 auto; min-height: 0; display: flex;
  align-items: center; justify-content: center;
}
/* Fill the stage in both directions. The viewBox and the default
   preserveAspectRatio letterbox it correctly, so this gives the largest map
   the screen can hold rather than one sized to a guess at the chrome. */
.hallmapv:fullscreen .hallmap,
.hallmapv.is-fullscreen .hallmap {
  width: 100%; height: 100%; max-height: none;
}

/* The hint is worth a line on a desktop and is pure waste on a landscape
   phone, where it costs a fifth of the map's height. */
.hallmapv:fullscreen .hallmapv__hint,
.hallmapv.is-fullscreen .hallmapv__hint { display: none; }

@media (orientation: landscape) and (max-height: 520px) {
  .hallmapv:fullscreen .hallmapv__bar,
  .hallmapv.is-fullscreen .hallmapv__bar { gap: 4px; }
  .hallmapv:fullscreen .hallmapv__caption,
  .hallmapv.is-fullscreen .hallmapv__caption { display: none; }
  .hallmapv:fullscreen .hallmapv__dock,
  .hallmapv.is-fullscreen .hallmapv__dock { padding: 7px 10px; margin-top: 4px; }
}
`;
